import classNames from "classnames";
import { first } from "lodash";
import Flatpickr from "react-flatpickr";
import type { DateTimePickerProps } from "react-flatpickr";
import { t } from "@/i18n-js/instance";
import { Icon } from "../../Icon";

export type DateInputOptions = NonNullable<DateTimePickerProps["options"]>;

type OnValueUpdate = DateTimePickerProps["onValueUpdate"];

export interface DateInputComponentProps {
  dataTestId?: string;
  dateInputOptions?: DateInputOptions;
  disabled?: boolean;
  fullWidth?: boolean;
  isStatic?: boolean;
  maxDate?: string;
  minDate?: string;
  minuteIncrement?: number;
  onChange: (date: string) => void;
  required?: boolean;
  value: string;
  withIcon?: boolean;
}

export const DateInputComponent = ({
  onChange,
  value,
  fullWidth = false,
  disabled,
  minDate,
  maxDate,
  required,
  withIcon,
  minuteIncrement,
  isStatic,
  dateInputOptions = {},
  dataTestId,
}: DateInputComponentProps) => {
  const onChangeDate: OnValueUpdate = dates => {
    if (first(dates)) {
      onChange(dates[0].toISOString());
    }
  };

  const options: DateInputOptions = {
    enableTime: true,
    dateFormat: "F d, Y G:i K",
    minDate: minDate,
    maxDate: maxDate,
    disableMobile: true,
    minuteIncrement: minuteIncrement,
    static: isStatic,
    ...dateInputOptions,
  };

  return (
    <div className="form-input" data-testid={dataTestId}>
      <div
        className={classNames("flex", {
          "flatpickr--static": isStatic,
        })}
      >
        <Flatpickr
          className={classNames(
            "flatpickr bg-primary border-primary text-dark h-[46px] px-4 text-left text-base font-normal",
            {
              "w-full": fullWidth,
              "!cursor-not-allowed opacity-40": disabled,
              "!rounded-r-none !border-r-0": withIcon,
            },
          )}
          data-enable-time
          options={options}
          value={value}
          onValueUpdate={onChangeDate}
          disabled={disabled}
          placeholder={t("select_a_date")}
          required={required}
        />
        {Boolean(withIcon) && (
          <div
            className={classNames(
              "bg-primary border-primary relative -ml-px inline-flex items-center space-x-2 rounded-r-lg border border-l-0 px-4 py-2",
              {
                "cursor-not-allowed opacity-40": disabled,
              },
            )}
          >
            <Icon
              type="16-calendar"
              useWithFillCurrentColor
              size={16}
              className="text-light"
            />
          </div>
        )}
      </div>
    </div>
  );
};
